import React, { useState, useEffect, useCallback } from 'react';
import painelApi, { getPainelUsuario, limparSessaoPainel } from '../services/painelApi';
import s from './Financeiro.module.css';
import MonitorSites from '../components/painel/MonitorSites';
import ClientesOperando from '../components/painel/ClientesOperando';

function fmtCnpjCpf(v) {
  if (!v) return '—';
  const d = v.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return v;
}

// Vencimento é o DIA FIXO do mês (ex.: 10 = "todo dia 10"), não uma data —
// cobrança recorrente. "Vencido" aqui é só visual: já passou do dia
// informado NESTE mês corrente; não tem memória de já ter recebido ou não
// (o painel não acompanha pagamento, só avisa pro admin decidir).
function diaVencido(diaVencimento) {
  if (!diaVencimento) return false;
  return new Date().getDate() >= diaVencimento;
}

// Estado calculado da licença (licencaService/licencaCalculo no backend) →
// badge visual. 'desconhecido' cobre tanto "nunca sincronizou" quanto
// "responsável sem sistema financeiro configurado ainda".
const BADGE_LICENCA = {
  ativa:         { emoji: '🟢', texto: 'Regular',      cor: '#166534', bg: '#dcfce7' },
  vencendo:      { emoji: '🟡', texto: 'Vencendo',     cor: '#92400e', bg: '#fef9c3' },
  em_tolerancia: { emoji: '🟠', texto: 'Em tolerância', cor: '#c2410c', bg: '#ffedd5' },
  suspensa:      { emoji: '🔴', texto: 'Suspensa',     cor: '#b91c1c', bg: '#fee2e2' },
  desconhecido:  { emoji: '⚪', texto: 'Sem dados',     cor: '#64748b', bg: '#f1f5f9' },
};

function BadgeLicenca({ licenca }) {
  const b = BADGE_LICENCA[licenca?.estado] || BADGE_LICENCA.desconhecido;
  return (
    <div title={licenca?.mensagem || ''}>
      <span style={{
        display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px',
        borderRadius: 10, color: b.cor, background: b.bg, whiteSpace: 'nowrap',
      }}>
        {b.emoji} {b.texto}
      </span>
      {licenca?.mensagem && (
        <div style={{ fontSize: 10.5, color: '#888', marginTop: 2, maxWidth: 170 }}>{licenca.mensagem}</div>
      )}
    </div>
  );
}

// ── Modal: gerenciar usuários do painel (só super_admin) ──────────────────
function ModalUsuarios({ usuarios, onFechar, onMudou }) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [superAdmin, setSuperAdmin] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [financeiro, setFinanceiro] = useState({}); // { [userId]: { url, apiKey } } — rascunho local
  const [redefinindoId, setRedefinindoId] = useState(null);
  const [novaSenha, setNovaSenha] = useState('');
  const [sucessoSenhaId, setSucessoSenhaId] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [edit, setEdit] = useState({ nome: '', email: '', superAdmin: false });
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const criar = async (e) => {
    e.preventDefault();
    if (!nome || !senha) return;
    setSalvando(true);
    setErro('');
    try {
      await painelApi.post('/painel-clientes/usuarios', { nome, email: email || null, senha, super_admin: superAdmin });
      setNome(''); setEmail(''); setSenha(''); setSuperAdmin(false);
      onMudou();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao criar usuário.');
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (u) => {
    await painelApi.put(`/painel-clientes/usuarios/${u.id}`, { ativo: !u.ativo });
    onMudou();
  };

  const abrirRedefinirSenha = (u) => { setRedefinindoId(u.id); setNovaSenha(''); setErro(''); setSucessoSenhaId(null); };
  const cancelarRedefinirSenha = () => { setRedefinindoId(null); setNovaSenha(''); };

  const confirmarRedefinirSenha = async (u) => {
    if (novaSenha.length < 6) { setErro('Senha precisa de ao menos 6 caracteres.'); return; }
    setErro('');
    try {
      await painelApi.put(`/painel-clientes/usuarios/${u.id}`, { senha: novaSenha });
      setRedefinindoId(null); setNovaSenha('');
      setSucessoSenhaId(u.id);
      setTimeout(() => setSucessoSenhaId(null), 3000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao redefinir a senha.');
    }
  };

  const abrirEditar = (u) => {
    setEditandoId(u.id);
    setEdit({ nome: u.nome, email: u.email || '', superAdmin: !!u.super_admin });
    setErro('');
  };
  const cancelarEditar = () => setEditandoId(null);

  const confirmarEditar = async (u) => {
    if (!edit.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    setErro(''); setSalvandoEdicao(true);
    try {
      await painelApi.put(`/painel-clientes/usuarios/${u.id}`, {
        nome: edit.nome.trim(), email: edit.email.trim() || null, super_admin: edit.superAdmin,
      });
      setEditandoId(null);
      onMudou();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar as alterações.');
    } finally { setSalvandoEdicao(false); }
  };

  const salvarFinanceiro = async (u) => {
    const rascunho = financeiro[u.id] || {};
    const body = {};
    if (rascunho.url !== undefined) body.financeiro_url = rascunho.url;
    if (rascunho.apiKey) body.financeiro_api_key = rascunho.apiKey;
    if (!Object.keys(body).length) return;
    await painelApi.put(`/painel-clientes/usuarios/${u.id}`, body);
    setFinanceiro(f => ({ ...f, [u.id]: { url: undefined, apiKey: '' } }));
    onMudou();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 8, width: 560, maxHeight: '85vh',
        overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,.35)' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border, #e2e8f0)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>Usuários do Painel</strong>
          <button className={s.btnErp} onClick={onFechar}>Fechar</button>
        </div>

        <div style={{ padding: '10px 18px' }}>
          {usuarios.map(u => (
            <div key={u.id} style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ fontSize: 13 }}>{u.nome}</strong>{' '}
                  <span style={{ fontSize: 11, color: '#888' }}>{u.email || '—'}</span>{' '}
                  {u.super_admin && <span style={{ fontSize: 11 }}>👑</span>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {editandoId !== u.id && (
                    <button className={s.btnErp} style={{ fontSize: 11, height: 22, padding: '0 8px' }}
                      onClick={() => abrirEditar(u)}>✏️ Editar</button>
                  )}
                  {redefinindoId !== u.id && (
                    <button className={s.btnErp} style={{ fontSize: 11, height: 22, padding: '0 8px' }}
                      onClick={() => abrirRedefinirSenha(u)}>
                      {sucessoSenhaId === u.id ? '✔ Senha redefinida' : '🔑 Redefinir senha'}
                    </button>
                  )}
                  <button className={s.btnErp} style={{ fontSize: 11, height: 22, padding: '0 8px' }}
                    onClick={() => alternarAtivo(u)}>
                    {u.ativo ? 'Ativo — desativar' : 'Inativo — ativar'}
                  </button>
                </div>
              </div>
              {editandoId === u.id && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input placeholder="Nome" autoFocus
                      value={edit.nome} onChange={e => setEdit(p => ({ ...p, nome: e.target.value }))}
                      style={{ flex: 1, height: 28, fontSize: 12, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
                    <input placeholder="E-mail (opcional)"
                      value={edit.email} onChange={e => setEdit(p => ({ ...p, email: e.target.value }))}
                      style={{ flex: 1, height: 28, fontSize: 12, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
                  </div>
                  <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={edit.superAdmin}
                      onChange={e => setEdit(p => ({ ...p, superAdmin: e.target.checked }))} />
                    super_admin (vê e mexe em todos os clientes)
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ fontSize: 11, height: 28 }}
                      onClick={() => confirmarEditar(u)} disabled={salvandoEdicao}>
                      {salvandoEdicao ? 'Salvando…' : 'Confirmar'}
                    </button>
                    <button className={s.btnErp} style={{ fontSize: 11, height: 28 }}
                      onClick={cancelarEditar} disabled={salvandoEdicao}>Cancelar</button>
                  </div>
                </div>
              )}
              {redefinindoId === u.id && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input type="password" placeholder="Nova senha (mín. 6 caracteres)" autoFocus
                    value={novaSenha} onChange={e => setNovaSenha(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && confirmarRedefinirSenha(u)}
                    style={{ flex: 1, height: 28, fontSize: 12, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ fontSize: 11, height: 28 }}
                    onClick={() => confirmarRedefinirSenha(u)}>Confirmar</button>
                  <button className={s.btnErp} style={{ fontSize: 11, height: 28 }}
                    onClick={cancelarRedefinirSenha}>Cancelar</button>
                </div>
              )}
              {/* Sistema financeiro deste responsável — usado pela integração
                  automática de licença (buscar/criar título de Contas a Receber
                  na instância dele). API key nunca vem do backend — só mostra
                  se já está configurada ou não. */}
              <div style={{ marginTop: 8, fontSize: 11, color: '#555' }}>
                Sistema financeiro: {u.tem_financeiro_api_key ? '✔ configurado' : '— não configurado'}
                {u.financeiro_url && <span style={{ color: '#888' }}> ({u.financeiro_url})</span>}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <input placeholder="https://rbdigital.emaisgestor.com.br"
                  value={financeiro[u.id]?.url ?? ''}
                  onChange={e => setFinanceiro(f => ({ ...f, [u.id]: { ...f[u.id], url: e.target.value } }))}
                  style={{ flex: 1, height: 26, fontSize: 11, padding: '0 6px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
                <input placeholder="API key" type="password"
                  value={financeiro[u.id]?.apiKey ?? ''}
                  onChange={e => setFinanceiro(f => ({ ...f, [u.id]: { ...f[u.id], apiKey: e.target.value } }))}
                  style={{ width: 120, height: 26, fontSize: 11, padding: '0 6px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
                <button className={s.btnErp} style={{ fontSize: 11, height: 26, padding: '0 8px' }}
                  onClick={() => salvarFinanceiro(u)}>Salvar</button>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={criar} style={{ padding: '10px 18px 18px', borderTop: '1px solid var(--border, #e2e8f0)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, margin: '8px 0' }}>+ Novo usuário</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input placeholder="Nome" value={nome} onChange={e => setNome(e.target.value)}
              style={{ height: 32, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
            <input placeholder="E-mail (opcional)" value={email} onChange={e => setEmail(e.target.value)}
              style={{ height: 32, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
            <input type="password" placeholder="Senha (mín. 6 caracteres)" value={senha} onChange={e => setSenha(e.target.value)}
              style={{ height: 32, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={superAdmin} onChange={e => setSuperAdmin(e.target.checked)} />
              super_admin (vê e mexe em todos os clientes)
            </label>
            {erro && <div style={{ color: '#b91c1c', fontSize: 12 }}>{erro}</div>}
            <button type="submit" disabled={salvando} className={`${s.btnErp} ${s.btnErpPrimario}`}>
              {salvando ? 'Criando…' : 'Criar usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal: usuários DO CLIENTE (o ERP dele, não painel_usuarios) — pra
// suporte redefinir senha em caso de "esqueci a senha" sem depender de
// outro admin ativo no próprio sistema do cliente. suporte_sistema=true
// (suporterb) nunca aparece aqui — o backend já filtra e nem deixa ser
// alvo mesmo chamando direto por id (mesma regra do Cadastro de Usuários
// normal: fica invisível pra todo mundo, só o próprio suporterb logado se
// enxerga via o perfil dele).
function ModalUsuariosCliente({ cliente, onFechar }) {
  const [usuarios, setUsuarios] = useState(null);
  const [erro, setErro] = useState('');
  const [redefinindoId, setRedefinindoId] = useState(null);
  const [novaSenha, setNovaSenha] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [sucessoId, setSucessoId] = useState(null);

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await painelApi.get(`/painel-clientes/${cliente.chave}/usuarios-cliente`);
      setUsuarios(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar usuários.');
    }
  }, [cliente.chave]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirRedefinir = (u) => { setRedefinindoId(u.id); setNovaSenha(''); setErro(''); setSucessoId(null); };
  const cancelarRedefinir = () => { setRedefinindoId(null); setNovaSenha(''); };

  const confirmarRedefinir = async (u) => {
    if (novaSenha.length < 6) { setErro('Senha precisa de ao menos 6 caracteres.'); return; }
    setSalvando(true); setErro('');
    try {
      await painelApi.post(`/painel-clientes/${cliente.chave}/usuarios-cliente/${u.id}/redefinir-senha`, { senha: novaSenha });
      setRedefinindoId(null); setNovaSenha('');
      setSucessoId(u.id);
      setTimeout(() => setSucessoId(null), 3000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao redefinir a senha.');
    } finally { setSalvando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 8, width: 520, maxHeight: '80vh',
        overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,.35)' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border, #e2e8f0)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>👤 Usuários — {cliente.cliente_nome}</strong>
          <button className={s.btnErp} onClick={onFechar}>Fechar</button>
        </div>

        <div style={{ padding: '10px 18px' }}>
          {erro && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{erro}</div>}
          {!usuarios ? (
            <div style={{ fontSize: 12, color: '#888' }}>Carregando…</div>
          ) : usuarios.length === 0 ? (
            <div style={{ fontSize: 12, color: '#888' }}>Nenhum usuário cadastrado neste cliente.</div>
          ) : (
            usuarios.map(u => (
              <div key={u.id} style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: 13 }}>{u.nome}</strong>{' '}
                    <span style={{ fontSize: 11, color: '#888' }}>{u.email || '—'}</span>{' '}
                    <span style={{ fontSize: 11 }}>{u.perfil}</span>
                    {!u.ativo && <span style={{ fontSize: 11, color: '#b91c1c' }}> — inativo</span>}
                  </div>
                  {redefinindoId !== u.id && (
                    <button className={s.btnErp} style={{ fontSize: 11, height: 22, padding: '0 8px' }}
                      onClick={() => abrirRedefinir(u)}>
                      {sucessoId === u.id ? '✔ Senha redefinida' : '🔑 Redefinir senha'}
                    </button>
                  )}
                </div>
                {redefinindoId === u.id && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input type="password" placeholder="Nova senha (mín. 6 caracteres)" autoFocus
                      value={novaSenha} onChange={e => setNovaSenha(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && confirmarRedefinir(u)}
                      style={{ flex: 1, height: 28, fontSize: 12, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
                    <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ fontSize: 11, height: 28 }}
                      onClick={() => confirmarRedefinir(u)} disabled={salvando}>
                      {salvando ? '...' : 'Confirmar'}
                    </button>
                    <button className={s.btnErp} style={{ fontSize: 11, height: 28 }}
                      onClick={cancelarRedefinir} disabled={salvando}>Cancelar</button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modal: gerar/confirmar cobrança de licença de um cliente ──────────────
function ModalGerarLicenca({ cliente, onFechar, onGerado }) {
  const hoje = new Date();
  const diaLimite = (cliente.dia_vencimento || 0) + (cliente.dias_tolerancia ?? 5);
  const [valor, setValor] = useState(cliente.valor_licenca ?? '');
  const [tolerancia, setTolerancia] = useState(cliente.dias_tolerancia ?? 5);
  const [contato, setContato] = useState(cliente.suporte_contato ?? '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const confirmar = async () => {
    if (!valor || Number(valor) <= 0) { setErro('Informe um valor válido.'); return; }
    setSalvando(true);
    setErro('');
    try {
      // Salva os parâmetros (tolerância/contato) junto, se mudaram — evita
      // uma segunda viagem só pra isso.
      await painelApi.post(`/painel-clientes/${cliente.chave}/parametros-licenca`, {
        dias_tolerancia: Number(tolerancia), valor_licenca: Number(valor), suporte_contato: contato || null,
      });
      await painelApi.post(`/painel-clientes/${cliente.chave}/gerar-licenca`, { valor: Number(valor) });
      onGerado();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gerar a licença.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 8, width: 420,
        boxShadow: '0 10px 40px rgba(0,0,0,.35)' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
          <strong>🪪 Gerar Licença</strong>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
          <div><strong>Cliente:</strong> {cliente.cliente_nome}</div>
          <div><strong>Responsável:</strong> {cliente.responsavel || '—'}</div>
          <div><strong>Vencimento:</strong> {cliente.dia_vencimento ? `dia ${cliente.dia_vencimento} (${hoje.getMonth() + 1}/${hoje.getFullYear()})` : 'não configurado'}</div>
          <div><strong>Data limite para bloqueio:</strong> {cliente.dia_vencimento ? `dia ${diaLimite > 28 ? diaLimite : diaLimite}` : '—'}</div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Valor da licença (R$)
            <input type="number" step="0.01" min="0.01" value={valor} onChange={e => setValor(e.target.value)}
              style={{ height: 32, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Tolerância (dias entre vencimento e bloqueio)
            <input type="number" min="0" max="30" value={tolerancia} onChange={e => setTolerancia(e.target.value)}
              style={{ height: 32, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Contato "Falar com Suporte" (link do WhatsApp deste cliente)
            <input placeholder="https://wa.me/55..." value={contato} onChange={e => setContato(e.target.value)}
              style={{ height: 32, padding: '0 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }} />
          </label>

          {erro && <div style={{ color: '#b91c1c', fontSize: 12 }}>{erro}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button className={s.btnErp} onClick={onFechar} disabled={salvando}>Cancelar</button>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={confirmar} disabled={salvando}>
              {salvando ? 'Gerando…' : 'Gerar Licença'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal: observações do cliente — nota livre pra registrar particularidades
// (acordo especial, contato preferencial, histórico relevante) que não têm
// campo próprio em lugar nenhum.
function ModalObservacoes({ cliente, onFechar, onSalvo }) {
  const [texto, setTexto] = useState(cliente.observacoes || '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      await painelApi.post(`/painel-clientes/${cliente.chave}/observacoes`, { observacoes: texto });
      onSalvo();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar a observação.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 8, width: 460,
        boxShadow: '0 10px 40px rgba(0,0,0,.35)' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
          <strong>📝 Observações — {cliente.cliente_nome}</strong>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
          <textarea
            autoFocus
            rows={6}
            placeholder="Particularidades deste cliente (acordo especial, contato preferencial, histórico relevante...)"
            value={texto}
            onChange={e => setTexto(e.target.value)}
            style={{ padding: 8, border: '1px solid var(--border, #cbd5e1)', borderRadius: 4,
              fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }}
          />
          {erro && <div style={{ color: '#b91c1c', fontSize: 12 }}>{erro}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className={s.btnErp} onClick={onFechar} disabled={salvando}>Cancelar</button>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Aba: Logs da Automação — vive na própria aba, não misturada com a
// lista de clientes. Quando `chavesVisiveis` vem preenchido (super_admin
// com "Mostrar todos" desmarcado), filtra os logs pro mesmo recorte da
// aba Clientes; null/undefined = sem filtro (super_admin com "Mostrar
// todos" marcado, ou usuário comum — que o backend já filtra sozinho).
function LogsAutomacao({ chavesVisiveis }) {
  const [logs, setLogs] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    painelApi.get('/painel-clientes/auditoria')
      .then(({ data }) => setLogs(data))
      .catch(() => setErro('Erro ao carregar os logs.'));
  }, []);

  const logsExibidos = chavesVisiveis ? (logs || []).filter(l => chavesVisiveis.has(l.site_chave)) : logs;

  return (
    <div style={{ marginTop: 10 }}>
      {erro && <div className={s.erroMsg}>{erro}</div>}
      {!logsExibidos ? <div style={{ fontSize: 12, color: '#888' }}>Carregando…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className={`${s.tabela} ${s.tabelaCompacta}`}>
            <thead><tr><th>Quando</th><th>Cliente</th><th>Ação</th><th>Detalhes</th><th>Operador</th></tr></thead>
            <tbody>
              {logsExibidos.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 16, color: '#888' }}>Nenhum evento registrado ainda.</td></tr>
              )}
              {logsExibidos.map(l => (
                <tr key={l.id}>
                  <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(l.criado_em).toLocaleString('pt-BR')}</td>
                  <td style={{ fontSize: 11 }}>{l.site_chave}</td>
                  <td style={{ fontSize: 11 }}>{l.acao}</td>
                  <td style={{ fontSize: 11 }}>{l.detalhes}</td>
                  <td style={{ fontSize: 11 }}>{l.operador_nome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Aba: Central de Chamados — fila do "Falar com Suporte" (chatbot de
// atendimento dos clientes do ERP), cruzando todos os sites. IA tenta
// triagem primeiro (quando ANTHROPIC_API_KEY estiver configurada); sem
// chave, cai direto aqui. Lista + thread + responder, tudo numa aba só —
// mesmo espírito enxuto do restante do Painel.
const BADGE_STATUS = {
  aberto:             { texto: 'Com a IA', cor: '#2563eb' },
  encaminhado_humano: { texto: 'Aguardando você', cor: '#d97706' },
  resolvido_ia:       { texto: 'Resolvido pela IA', cor: '#16a34a' },
  fechado:            { texto: 'Encerrado', cor: '#6b7280' },
};

function CentralChamados() {
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState('');
  const [selecionadoId, setSelecionadoId] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [resposta, setResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [apenasAguardando, setApenasAguardando] = useState(true);

  const carregarLista = useCallback(async () => {
    try {
      const { data } = await painelApi.get('/painel-clientes/chamados');
      setLista(data);
    } catch {
      setErro('Erro ao carregar os chamados.');
    }
  }, []);

  useEffect(() => {
    carregarLista();
    // Atualiza a fila sozinha a cada 20s — pra um chamado novo aparecer sem
    // precisar recarregar a página manualmente.
    const t = setInterval(carregarLista, 20000);
    return () => clearInterval(t);
  }, [carregarLista]);

  const abrirChamado = async (id) => {
    setSelecionadoId(id);
    setDetalhe(null);
    try {
      const { data } = await painelApi.get(`/painel-clientes/chamados/${id}`);
      setDetalhe(data);
    } catch {
      setErro('Erro ao abrir o chamado.');
    }
  };

  const enviarResposta = async (e) => {
    e.preventDefault();
    const texto = resposta.trim();
    if (!texto || enviando) return;
    setEnviando(true);
    try {
      await painelApi.post(`/painel-clientes/chamados/${selecionadoId}/responder`, { mensagem: texto });
      setResposta('');
      await abrirChamado(selecionadoId);
      await carregarLista();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao responder.');
    } finally {
      setEnviando(false);
    }
  };

  const fecharChamado = async () => {
    if (!window.confirm('Encerrar esse chamado? O cliente não vai conseguir mandar mais mensagem nele.')) return;
    try {
      await painelApi.post(`/painel-clientes/chamados/${selecionadoId}/fechar`);
      await abrirChamado(selecionadoId);
      await carregarLista();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao encerrar.');
    }
  };

  const listaExibida = apenasAguardando
    ? (lista || []).filter(c => c.status === 'encaminhado_humano')
    : lista;

  return (
    <div style={{ marginTop: 10 }}>
      {erro && <div className={s.erroMsg}>{erro}</div>}
      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <input type="checkbox" checked={apenasAguardando} onChange={e => setApenasAguardando(e.target.checked)} />
        Mostrar só aguardando resposta
      </label>
      {!listaExibida ? <div style={{ fontSize: 12, color: '#888' }}>Carregando…</div> : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 340px', maxHeight: 560, overflowY: 'auto', border: '1px solid var(--border, #ddd)', borderRadius: 8 }}>
            {listaExibida.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: '#888', textAlign: 'center' }}>Nenhum chamado por aqui.</div>
            )}
            {listaExibida.map(c => (
              <div
                key={c.id}
                onClick={() => abrirChamado(c.id)}
                style={{
                  padding: '10px 12px', borderBottom: '1px solid var(--border, #eee)', cursor: 'pointer',
                  background: c.id === selecionadoId ? 'var(--primary-light, #eff6ff)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700 }}>
                  <span>{c.site_nome}</span>
                  <span style={{ color: BADGE_STATUS[c.status]?.cor, fontWeight: 700 }}>{BADGE_STATUS[c.status]?.texto || c.status}</span>
                </div>
                <div style={{ fontSize: 11, color: '#888', margin: '2px 0' }}>{c.usuario_nome} · {c.departamento}</div>
                <div style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.ultima_mensagem}</div>
              </div>
            ))}
          </div>

          <div style={{ flex: 1, border: '1px solid var(--border, #ddd)', borderRadius: 8, minHeight: 400, display: 'flex', flexDirection: 'column' }}>
            {!detalhe ? (
              <div style={{ padding: 24, fontSize: 13, color: '#888', textAlign: 'center' }}>Selecione um chamado à esquerda.</div>
            ) : (
              <>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border, #eee)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{detalhe.site_nome}</strong> — {detalhe.usuario_nome} · {detalhe.departamento}
                  </div>
                  {detalhe.status !== 'fechado' && (
                    <button className={s.btnErp} style={{ fontSize: 11 }} onClick={fecharChamado}>Encerrar</button>
                  )}
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420 }}>
                  {detalhe.mensagens.map((m, i) => (
                    <div key={i} style={{
                      alignSelf: m.remetente === 'cliente' ? 'flex-start' : 'flex-end',
                      maxWidth: '75%',
                      background: m.remetente === 'cliente' ? '#f1f3f5' : m.remetente === 'suporte' ? '#2563eb' : '#fef3c7',
                      color: m.remetente === 'suporte' ? '#fff' : m.remetente === 'sistema' ? '#92400e' : '#111',
                      padding: '8px 12px', borderRadius: 10, fontSize: 13, whiteSpace: 'pre-wrap',
                    }}>
                      {m.texto}
                    </div>
                  ))}
                </div>
                {detalhe.status !== 'fechado' ? (
                  <form onSubmit={enviarResposta} style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--border, #eee)' }}>
                    <input
                      value={resposta}
                      onChange={e => setResposta(e.target.value)}
                      placeholder="Responder..."
                      disabled={enviando}
                      style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--border, #ccc)', borderRadius: 6 }}
                      autoFocus
                    />
                    <button className={s.btnErp} type="submit" disabled={enviando || !resposta.trim()}>Enviar</button>
                  </form>
                ) : (
                  <div style={{ padding: 10, fontSize: 12, color: '#888', textAlign: 'center', borderTop: '1px solid var(--border, #eee)' }}>Chamado encerrado.</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PainelClientes({ onSair }) {
  const usuarioLogado = getPainelUsuario();
  const [lista, setLista] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [alterando, setAlterando] = useState(null);
  const [modalUsuarios, setModalUsuarios] = useState(false);
  const [modalUsuariosCliente, setModalUsuariosCliente] = useState(null); // cliente selecionado | null
  const [modalLicenca, setModalLicenca] = useState(null); // cliente selecionado | null
  const [modalObservacoes, setModalObservacoes] = useState(null); // cliente selecionado | null
  const [aba, setAba] = useState('clientes'); // 'clientes' | 'logs' | 'chamados' — abas separadas, não misturadas
  // super_admin (suporterb/ze) por padrão só vê os PRÓPRIOS clientes vinculados,
  // igual usuário comum; "Mostrar todos" marcado enxerga o painel inteiro.
  const [mostrarTodos, setMostrarTodos] = useState(false);

  const trata401 = (err) => {
    if (err.response?.status === 401) { limparSessaoPainel(); onSair(); return true; }
    return false;
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const { data } = await painelApi.get('/painel-clientes');
      setLista(data);
      if (usuarioLogado?.super_admin) {
        const { data: us } = await painelApi.get('/painel-clientes/usuarios');
        setUsuarios(us);
      }
    } catch (err) {
      if (!trata401(err)) setErro('Erro ao carregar os clientes.');
    } finally {
      setCarregando(false);
    }
  }, [usuarioLogado?.super_admin]); // eslint-disable-line

  useEffect(() => { carregar(); }, [carregar]);

  const alternarStatus = async (c) => {
    const vaiAtivar = !c.ativo;
    const aviso = vaiAtivar
      ? `Reativar o acesso de "${c.cliente_nome}"?`
      : `Suspender o acesso de "${c.cliente_nome}"?\n\nO login desse cliente passa a ser recusado imediatamente para NOVOS acessos — sessões já abertas continuam válidas até expirar (até 8h).`;
    if (!window.confirm(aviso)) return;
    setAlterando(c.chave);
    try {
      await painelApi.post(`/painel-clientes/${c.chave}/status`, { ativo: vaiAtivar });
      await carregar();
    } catch (err) {
      if (!trata401(err)) alert(err.response?.data?.erro || 'Erro ao alterar status.');
    } finally {
      setAlterando(null);
    }
  };

  const salvarDiaVencimento = async (chave, valor) => {
    const dia = valor === '' ? null : parseInt(valor, 10);
    if (dia != null && (isNaN(dia) || dia < 1 || dia > 31)) {
      alert('Informe um dia entre 1 e 31 (ou deixe em branco).');
      return;
    }
    setAlterando(chave);
    try {
      await painelApi.post(`/painel-clientes/${chave}/vencimento`, { dia_vencimento: dia });
      await carregar();
    } catch (err) {
      if (!trata401(err)) alert(err.response?.data?.erro || 'Erro ao salvar o vencimento.');
    } finally {
      setAlterando(null);
    }
  };

  const vincular = async (chave, painelUsuarioId) => {
    setAlterando(chave);
    try {
      await painelApi.post(`/painel-clientes/${chave}/vincular`, { painel_usuario_id: painelUsuarioId || null });
      await carregar();
    } catch (err) {
      if (!trata401(err)) alert(err.response?.data?.erro || 'Erro ao vincular.');
    } finally {
      setAlterando(null);
    }
  };

  const sincronizarUm = async (chave) => {
    setAlterando(chave);
    try {
      await painelApi.post(`/painel-clientes/${chave}/sincronizar-licenca`);
      await carregar();
    } catch (err) {
      if (!trata401(err)) alert(err.response?.data?.erro || 'Erro ao sincronizar a licença.');
    } finally {
      setAlterando(null);
    }
  };

  const salvarParametro = async (chave, campo, valor) => {
    setAlterando(chave);
    try {
      await painelApi.post(`/painel-clientes/${chave}/parametros-licenca`, { [campo]: valor });
      await carregar();
    } catch (err) {
      if (!trata401(err)) alert(err.response?.data?.erro || 'Erro ao salvar.');
    } finally {
      setAlterando(null);
    }
  };

  const sair = () => { limparSessaoPainel(); onSair(); };

  // super_admin com "Mostrar todos" desmarcado enxerga só o que está
  // vinculado a ele mesmo (mesmo recorte de um usuário comum); marcado,
  // ou usuário comum (que o backend já filtra), mostra a lista inteira.
  const listaExibida = (usuarioLogado?.super_admin && !mostrarTodos)
    ? (lista || []).filter(c => c.responsavel === usuarioLogado?.nome)
    : lista;
  const chavesVisiveis = (usuarioLogado?.super_admin && !mostrarTodos)
    ? new Set((listaExibida || []).map(c => c.chave))
    : null;

  // Resumo por estado de licença — sobre o que está sendo exibido (respeita
  // o filtro "Mostrar todos"), não faz requisição nova.
  const resumo = listaExibida?.reduce((acc, c) => {
    const estado = c.licenca?.estado || 'desconhecido';
    acc[estado] = (acc[estado] || 0) + 1;
    acc.total += 1;
    return acc;
  }, { total: 0 });

  // Soma o Valor Contrato do que está sendo exibido (mesmo recorte do
  // "Mostrar todos") — não conta cliente indisponível/sem valor cadastrado.
  const totalContrato = (listaExibida || []).reduce((soma, c) => soma + (Number(c.valor_licenca) || 0), 0);

  return (
    // .erpWrap tem margin negativo (-20px -24px) pensado pra "sangrar" até a
    // borda DENTRO do padding do Layout principal do ERP — como esta tela é
    // uma área independente (sem esse Layout ao redor), o negativo puxava o
    // conteúdo pra fora da viewport à esquerda/topo. Zerado aqui.
    <div className={s.erpWrap} style={{ minHeight: '100vh', height: 'auto', margin: 0 }}>
      <div className={s.titleBar} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span><span>🏢</span> Painel de Clientes</span>
        <span style={{ fontSize: 12, fontWeight: 400, display: 'flex', alignItems: 'center', gap: 10 }}>
          {usuarioLogado?.nome} {usuarioLogado?.super_admin ? '👑' : ''}
          <button className={s.btnErp} onClick={sair}>Sair</button>
        </span>
      </div>
      <div className={s.toolbar} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button className={s.btnErp} style={aba === 'clientes' ? { fontWeight: 700, borderBottom: '2px solid var(--primary)' } : {}}
          onClick={() => setAba('clientes')}>🏢 Clientes</button>
        <button className={s.btnErp} style={aba === 'logs' ? { fontWeight: 700, borderBottom: '2px solid var(--primary)' } : {}}
          onClick={() => setAba('logs')}>📋 Logs da Automação</button>
        <button className={s.btnErp} style={aba === 'chamados' ? { fontWeight: 700, borderBottom: '2px solid var(--primary)' } : {}}
          onClick={() => setAba('chamados')}>💬 Central de Chamados</button>
        {usuarioLogado?.super_admin && (
          <button className={s.btnErp} style={aba === 'operando' ? { fontWeight: 700, borderBottom: '2px solid var(--primary)' } : {}}
            onClick={() => setAba('operando')}>🟢 Operando</button>
        )}
        {usuarioLogado?.super_admin && (
          <button className={s.btnErp} style={aba === 'monitor' ? { fontWeight: 700, borderBottom: '2px solid var(--primary)' } : {}}
            onClick={() => setAba('monitor')}>🩺 Monitoramento</button>
        )}
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
        <button className={s.btnErp} onClick={carregar} disabled={carregando}>
          {carregando ? 'Atualizando...' : '↻ Atualizar'}
        </button>
        {usuarioLogado?.super_admin && (
          <button className={s.btnErp} onClick={() => setModalUsuarios(true)}>👤 Usuários do Painel</button>
        )}
        {usuarioLogado?.super_admin && (
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
            <input type="checkbox" checked={mostrarTodos} onChange={e => setMostrarTodos(e.target.checked)} />
            Mostrar todos
          </label>
        )}
      </div>

      {aba === 'clientes' && resumo && resumo.total > 0 && (
        <div style={{ display: 'flex', gap: 16, padding: '6px 4px 10px', fontSize: 12, flexWrap: 'wrap' }}>
          <span>Clientes: <strong>{resumo.total}</strong></span>
          <span>{BADGE_LICENCA.ativa.emoji} Regulares: <strong>{resumo.ativa || 0}</strong></span>
          <span>{BADGE_LICENCA.vencendo.emoji} Vencendo: <strong>{resumo.vencendo || 0}</strong></span>
          <span>{BADGE_LICENCA.em_tolerancia.emoji} Em tolerância: <strong>{resumo.em_tolerancia || 0}</strong></span>
          <span>{BADGE_LICENCA.suspensa.emoji} Suspensas: <strong>{resumo.suspensa || 0}</strong></span>
        </div>
      )}

      {aba === 'clientes' && (
        <div style={{ fontSize: 11, color: '#888', padding: '0 4px 8px' }}>
          Suspender bloqueia NOVOS logins do cliente imediatamente — sessões já abertas continuam válidas
          até o token expirar. Vencimento é o dia fixo do mês em que a cobrança vence (ex.: 10 = todo dia
          10) — a coluna LICENÇA é calculada automaticamente a partir do sistema financeiro do responsável;
          suspender/ativar manualmente continua disponível e sempre tem prioridade sobre a automação.
          {!usuarioLogado?.super_admin && ' Você só vê os clientes vinculados a você.'}
          {usuarioLogado?.super_admin && !mostrarTodos && ' Mostrando só os seus clientes — marque "Mostrar todos" pra ver os de todo mundo.'}
        </div>
      )}

      {erro && <div className={s.erroMsg}>{erro}</div>}

      {aba === 'clientes' && listaExibida && (
        <div style={{ overflowX: 'auto', marginTop: 6 }}>
          <table className={`${s.tabela} ${s.tabelaCompacta}`}>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Qtd. Acessos</th>
                <th style={{ textAlign: 'center' }}>Qtd. Simultân.</th>
                <th>Valor Contrato</th>
                <th>Vencimento (dia)</th>
                <th>Licença</th>
                {usuarioLogado?.super_admin && <th>Responsável</th>}
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {listaExibida.length === 0 && (
                <tr><td colSpan={usuarioLogado?.super_admin ? 9 : 8} style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                  Nenhum cliente vinculado a você ainda.
                </td></tr>
              )}
              {listaExibida.map(c => {
                const vencido = diaVencido(c.dia_vencimento);
                return (
                  <tr key={c.chave}>
                    <td>
                      <div style={{ fontWeight: 'bold' }}>{c.cliente_nome}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{c.dominio}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{fmtCnpjCpf(c.cnpj_cpf)}</div>
                    </td>
                    <td>
                      {c.ativo == null ? (
                        <span style={{ fontSize: 11, color: '#b91c1c' }} title={c.erro}>⚠ indisponível</span>
                      ) : (
                        <span className={`${s.badge} ${c.ativo ? s.badgeAtivo : s.badgeInativo}`}>
                          {c.ativo ? 'ATIVO' : 'SUSPENSO'}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>{c.qtd_acessos ?? '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="number" min="0" step="1" placeholder="—"
                        style={{ width: 56, height: 26, fontSize: 12, textAlign: 'center',
                          padding: '2px 4px', border: '1px solid var(--border)', borderRadius: 4,
                          background: 'var(--card-bg)', color: 'inherit' }}
                        defaultValue={c.qtd_simultaneos ?? ''}
                        disabled={c.ativo == null || alterando === c.chave}
                        title="Quantidade de acessos simultâneos contratada"
                        onBlur={e => {
                          const atual = c.qtd_simultaneos ?? '';
                          if (e.target.value !== String(atual)) {
                            salvarParametro(c.chave, 'qtd_simultaneos', e.target.value === '' ? null : parseInt(e.target.value, 10));
                          }
                        }} />
                      {usuarioLogado?.super_admin && (
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                          fontSize: 10, marginTop: 3, color: c.qtd_simultaneos_ativo ? '#166534' : '#888', cursor: 'pointer' }}
                          title="Marcado: o sistema BLOQUEIA login de verdade ao atingir esse número (mostra aviso de suporte pro próximo que tentar entrar). Desmarcado: o número é só informativo, não bloqueia nada — igual hoje.">
                          <input type="checkbox" checked={!!c.qtd_simultaneos_ativo}
                            disabled={c.ativo == null || c.qtd_simultaneos == null || alterando === c.chave}
                            onChange={e => salvarParametro(c.chave, 'qtd_simultaneos_ativo', e.target.checked)} />
                          bloquear
                        </label>
                      )}
                    </td>
                    <td>
                      <input type="number" min="0" step="0.01" placeholder="R$"
                        style={{ width: 84, height: 26, fontSize: 12,
                          padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 4,
                          background: 'var(--card-bg)', color: 'inherit' }}
                        defaultValue={c.valor_licenca ?? ''}
                        disabled={c.ativo == null || alterando === c.chave}
                        title="Valor do contrato — preenche o modal de Gerar Licença"
                        onBlur={e => {
                          const atual = c.valor_licenca ?? '';
                          if (e.target.value !== String(atual)) {
                            salvarParametro(c.chave, 'valor_licenca', e.target.value === '' ? null : Number(e.target.value));
                          }
                        }} />
                    </td>
                    <td>
                      <input type="number" min="1" max="31" placeholder="dia"
                        style={{ width: 60, height: 26, fontSize: 12,
                          padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 4,
                          background: 'var(--card-bg)', color: 'inherit',
                          ...(vencido && c.ativo ? { borderColor: '#dc2626', color: '#b91c1c', fontWeight: 600 } : {}) }}
                        defaultValue={c.dia_vencimento ?? ''}
                        disabled={c.ativo == null || alterando === c.chave}
                        title={vencido && c.ativo ? `Já passou do dia ${c.dia_vencimento} deste mês` : 'Dia do mês em que a cobrança vence (1-31)'}
                        onBlur={e => {
                          const atual = c.dia_vencimento ?? '';
                          if (e.target.value !== String(atual)) salvarDiaVencimento(c.chave, e.target.value);
                        }} />
                    </td>
                    <td>
                      <BadgeLicenca licenca={c.licenca} />
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <button className={s.btnErp} style={{ fontSize: 10, height: 20, padding: '0 6px' }}
                          disabled={alterando === c.chave} onClick={() => sincronizarUm(c.chave)}
                          title="Sincronizar com o sistema financeiro do responsável">🔄</button>
                        <button className={s.btnErp} style={{ fontSize: 10, height: 20, padding: '0 6px' }}
                          onClick={() => setModalLicenca(c)}>🪪 Gerar</button>
                      </div>
                    </td>
                    {usuarioLogado?.super_admin && (
                      <td>
                        <select
                          value={usuarios.find(u => u.nome === c.responsavel)?.id || ''}
                          disabled={alterando === c.chave}
                          onChange={e => vincular(c.chave, e.target.value ? parseInt(e.target.value) : null)}
                          style={{ height: 26, fontSize: 12, border: '1px solid var(--border)', borderRadius: 4 }}>
                          <option value="">— ninguém —</option>
                          {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                        </select>
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className={s.btnErp}
                          style={c.ativo ? { color: '#b91c1c' } : { color: '#166534' }}
                          disabled={c.ativo == null || alterando === c.chave}
                          onClick={() => alternarStatus(c)}>
                          {alterando === c.chave ? '...' : c.ativo ? 'Suspender' : 'Ativar'}
                        </button>
                        <button className={s.btnErp} disabled={c.ativo == null}
                          onClick={() => setModalUsuariosCliente(c)} title="Ver usuários deste cliente e redefinir senha">
                          👤 Usuários
                        </button>
                        <button className={s.btnErp}
                          style={c.observacoes ? { color: '#92400e' } : {}}
                          onClick={() => setModalObservacoes(c)}
                          title={c.observacoes || 'Registrar particularidades deste cliente'}>
                          📝{c.observacoes ? ' Nota' : ''}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {listaExibida.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Total (Valor Contrato):</td>
                  <td style={{ fontWeight: 700 }}>
                    {totalContrato.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </td>
                  <td colSpan={usuarioLogado?.super_admin ? 4 : 3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {aba === 'logs' && <LogsAutomacao chavesVisiveis={chavesVisiveis} />}
      {aba === 'chamados' && <CentralChamados />}
      {aba === 'operando' && usuarioLogado?.super_admin && <ClientesOperando />}
      {aba === 'monitor' && usuarioLogado?.super_admin && <MonitorSites />}

      {modalUsuarios && (
        <ModalUsuarios usuarios={usuarios} onFechar={() => setModalUsuarios(false)} onMudou={carregar} />
      )}

      {modalUsuariosCliente && (
        <ModalUsuariosCliente cliente={modalUsuariosCliente} onFechar={() => setModalUsuariosCliente(null)} />
      )}

      {modalLicenca && (
        <ModalGerarLicenca
          cliente={modalLicenca}
          onFechar={() => setModalLicenca(null)}
          onGerado={() => { setModalLicenca(null); carregar(); }}
        />
      )}

      {modalObservacoes && (
        <ModalObservacoes
          cliente={modalObservacoes}
          onFechar={() => setModalObservacoes(null)}
          onSalvo={() => { setModalObservacoes(null); carregar(); }}
        />
      )}
    </div>
  );
}
